import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AgentFunctionEditorAdvanced, FilesState } from "../../components/agents/AgentFunctionEditorAdvanced";
// import { PendingFunctionApprovals } from "../../components/PendingFunctionApprovals";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useSearchParam } from "../../hooks/useSearchParam";
import { featureActions } from "../../redux/reducers/featureSlice";
import { Icon } from "../../components/base/Icon";
import { View, Text, Button, InputField, NavLink, Dialog } from "../../components/base";
import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { agentActions } from "../../redux/reducers/agentSlice";

export const PYTHON_FUNCTION_TEMPLATE = `
async def handler(event, context):
    yield TextToSpeechEvent(text="Hello, world!", voice="lyric")
`.trim();

export const JAVASCRIPT_FUNCTION_TEMPLATE = `
async function handler(event, context, emit) {
  emit(new TextToSpeechEvent("Hello, world!", "lyric"));
};
`.trim();

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

export const AgentFunctionPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const functionId = useSearchParam("f");

  const isNewAgentMode = !agentId;

  const agentFunction = useAppSelector((state) => (functionId ? state.agentFunction.entities[functionId] : null));
  const agentFunctions = useAppSelector((state) => Object.values(state.agentFunction.entities));
  const agentFunctionFormData = useAppSelector((state) => state.feature.agentFunctionFormData);
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));

  const [initialCode, setInitialCode] = useState<string | undefined>();
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFunctionId, setPendingFunctionId] = useState<string | undefined>();
  const [isCreating, setIsCreating] = useState(false);
  const [showNewVersionDialog, setShowNewVersionDialog] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>();

  useEffect(() => {
    if (agentFunction && agentFunction.agentId === agentId) {
      // Ensure multifile functions have the isMultifile flag set
      const functionData = {
        ...agentFunction,
        isMultifile: agentFunction.codePath ? true : agentFunction.isMultifile || false,
        ...(agentFunctionFormData.activeFile && { activeFile: agentFunctionFormData.activeFile }),
      };
      dispatch(featureActions.setAgentFunctionFormData(functionData));
      setInitialCode(agentFunction.code);
      setIsDirty(false);
    } else {
      dispatch(featureActions.setAgentFunctionFormData({}));
      setInitialCode(undefined);
      setIsDirty(false);
    }
  }, [agentFunction, dispatch, agentId, agentFunctionFormData.activeFile]);

  useEffect(() => {
    if (!isNewAgentMode && !functionId && agentFunctions.length > 0) {
      navigate(`/agents/${agentId}/function?f=${agentFunctions[0].id}`);
    }
  }, [functionId, agentFunctions, isNewAgentMode, navigate, agentId]);

  useEffect(() => {
    setIsDirty(initialCode !== undefined && agentFunctionFormData.code !== initialCode);
  }, [agentFunctionFormData.code, initialCode]);

  useEffect(() => {
    if (!isNewAgentMode && pendingFunctionId && !showConfirmDialog) {
      navigate(`/agents/${agentId}/function?f=${pendingFunctionId}`);
      setPendingFunctionId(undefined);
    }
  }, [pendingFunctionId, showConfirmDialog, navigate, agentId, isNewAgentMode]);

  useEffect(() => {
    if (!isNewAgentMode && functionId !== agentFunction?.id && isDirty && !pendingFunctionId) {
      setShowConfirmDialog(true);
      setPendingFunctionId(functionId);
      // Prevent immediate switch
      navigate(`/agents/${agentId}/function?f=${agentFunction?.id || functionId}`, { replace: true });
    }
  }, [functionId, agentFunction?.id, isDirty, pendingFunctionId, navigate, agentId, isNewAgentMode]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to reload?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Multi-file editor onChange
  const onFilesChange = (files: FilesState) => {
    dispatch(featureActions.setAgentFunctionFiles(files));
  };

  // Active file change for multi-file editor
  const onActiveFileChange = (filename: string) => {
    dispatch(featureActions.setAgentFunctionActiveFile(filename));
  };

  const onClickCreateFunctionHandler = (language: string) => async () => {
    // Show dialog to get function name first
    setShowNewVersionDialog(true);
    setNewVersionName("");
    setSelectedLanguage(language);
  };

  const handleCreateFunction = async () => {
    try {
      setIsCreating(true);

      const newFunction = await dispatch(
        agentFunctionActions.create({
          agentId,
          language: selectedLanguage!,
          displayName: newVersionName.trim(),
          code: selectedLanguage === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
        }),
      ).unwrap();

      // Close dialog and reset state
      setShowNewVersionDialog(false);
      setNewVersionName("");
      setSelectedLanguage(undefined);

      // Navigate to the new function
      navigate(`/agents/${agentId}/function?f=${newFunction.id}`);
    } catch (error) {
      console.error("Failed to create function:", error);
      alert("Failed to create function. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelNewVersion = () => {
    setShowNewVersionDialog(false);
    setNewVersionName("");
    setSelectedLanguage(undefined);
  };

  const handleCreateAgent = async () => {
    try {
      setIsCreating(true);

      const newAgent = await dispatch(
        agentActions.create({
          name: agentFunctionFormData.agentName!.trim(),
          description: agentFunctionFormData.agentDescription?.trim() || "",
          defaultVoiceId: "lyric", // Default voice
          strategy: "cascade", // Default strategy
        }),
      ).unwrap();

      // Navigate to the new agent's details page
      navigate(`/agents/${newAgent.id}/overview`);
    } catch (error) {
      console.error("Failed to create agent:", error);
      alert("Failed to create agent. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    navigate("/agents");
  };

  // Determine which editor to show based on function type
  const isDeployed = agentEnvironments.some((env) => env.functionId === functionId);

  // Find the latest version (most recently created)
  const latestFunction =
    agentFunctions.length > 0
      ? agentFunctions.reduce((latest, current) => (new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest))
      : null;

  // Find the latest non-deployed version
  const deployedFunctionIds = agentEnvironments.map((env) => env.functionId);
  const nonDeployedFunctions = agentFunctions.filter((fn) => !deployedFunctionIds.includes(fn.id));
  const latestNonDeployedFunction =
    nonDeployedFunctions.length > 0
      ? nonDeployedFunctions.reduce((latest, current) => (new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest))
      : null;

  // A function is read-only if it's deployed (and not debug) OR if it's not the latest version
  const isReadOnly =
    (isDeployed && !agentEnvironments.some((env) => env.functionId === functionId && env.isDebug)) ||
    (latestFunction && functionId !== latestFunction.id) ||
    false;

  // Check if the new version name is unique
  const isNameUnique = !agentFunctions.some((fn) => (fn.displayName || fn.name).toLowerCase() === newVersionName.trim().toLowerCase());
  const canCreateVersion = newVersionName.trim().length > 0 && isNameUnique;

  return (
    <motion.div
      className="flex flex-col flex-1 h-full overflow-hidden w-full"
      variants={itemVariants}
    >
      {/* Auto-Optimize feature disabled - uncomment when ready */}
      {/* <PendingFunctionApprovals
        agentId={agentId || ""}
        currentFunctionId={functionId}
        onFunctionSelect={(functionId) => navigate(`/agents/${agentId}/function?f=${functionId}`)}
        onNavigateToFunction={(functionId) => navigate(`/agents/${agentId}/function?f=${functionId}`)}
      /> */}
      {isNewAgentMode ? (
        <View className="flex flex-col flex-1 w-full gap-6 p-6 bg-white dark:bg-gray-900 overflow-y-auto">
          <NavLink
            href="/agents"
            className="flex items-center w-fit p-0"
          >
            <Icon
              name="arrow-back"
              size={18}
              className="mr-1"
            />
            Agents
          </NavLink>

          <View className="flex flex-col gap-6 w-full">
            <View className="flex flex-col gap-2">
              <Text className="text-2xl font-semibold text-gray-900 dark:text-white">Create Agent</Text>
              <Text className="text-sm text-gray-600 dark:text-gray-400">Create a new AI agent with custom voice and personality.</Text>
            </View>

            <View className="flex flex-col gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm w-full">
              <InputField
                label="Agent Name"
                placeholder="Give your agent a descriptive name"
                value={agentFunctionFormData.agentName || ""}
                onChange={(value) => dispatch(featureActions.setAgentFunctionAgentName(value))}
              />
              <InputField
                label="Description"
                placeholder="Describe what your agent does"
                value={agentFunctionFormData.agentDescription || ""}
                onChange={(value) => dispatch(featureActions.setAgentFunctionAgentDescription(value))}
                type="textarea"
                className="min-h-[80px]"
              />
            </View>

            <View className="flex flex-row gap-3 justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="brand"
                onClick={handleCreateAgent}
                disabled={isCreating || !agentFunctionFormData.agentName?.trim()}
                isLoading={isCreating}
              >
                <Icon
                  name="add"
                  size={16}
                  className="mr-2"
                />
                Create Agent
              </Button>
            </View>
          </View>
        </View>
      ) : !agentFunctionFormData?.code ? (
        <View className="flex flex-col gap-4 h-full p-8">
          <Text className="text-xl font-semibold">Create a new function</Text>
          <View className="grid grid-cols-1 md:grid-cols-2 gap-8 flex-1">
            <View
              className="bg-white dark:bg-gray-800 rounded-lg cursor-pointer transition-all flex flex-col h-full hover:scale-[1.01]"
              onClick={onClickCreateFunctionHandler("python")}
            >
              <View className="flex flex-col items-center justify-center p-8 border-2 rounded-lg h-full hover:!border-brand border-gray-200 dark:border-gray-700 transition-all duration-300">
                <Icon
                  name="python"
                  size={64}
                  className="mb-6 text-blue-500"
                />
                <Text className="text-2xl font-bold mb-4">Python</Text>
                <Text className="text-gray-500 dark:text-gray-400 text-center">
                  Create a Python function for your agent with full access to Python libraries and ecosystem
                </Text>
              </View>
            </View>

            <View
              className="bg-white dark:bg-gray-800 rounded-lg cursor-pointer transition-all flex flex-col h-full hover:scale-[1.01]"
              onClick={onClickCreateFunctionHandler("javascript")}
            >
              <View className="flex flex-col items-center justify-center p-8 border-2 rounded-lg h-full hover:!border-brand border-gray-200 dark:border-gray-700 transition-all duration-300">
                <Icon
                  name="javascript"
                  size={64}
                  className="mb-6 text-yellow-400"
                />
                <Text className="text-2xl font-bold mb-4">JavaScript</Text>
                <Text className="text-gray-500 dark:text-gray-400 text-center">
                  Create a JavaScript function for your agent with access to Node.js modules and APIs
                </Text>
              </View>
            </View>
          </View>
        </View>
      ) : (
        // Use the new advanced editor for all code editing
        <AgentFunctionEditorAdvanced
          code={agentFunctionFormData?.code}
          isMultifile={!!agentFunctionFormData?.codePath}
          activeFile={agentFunctionFormData.activeFile}
          onChange={onFilesChange}
          onActiveFileChange={onActiveFileChange}
          readOnly={isReadOnly}
          language={agentFunctionFormData.language || "python"}
          functionId={functionId}
          latestNonDeployedVersionName={latestNonDeployedFunction?.displayName || latestNonDeployedFunction?.name}
        />
      )}

      {/* New Version Dialog for Function Creation */}
      {showNewVersionDialog && (
        <Dialog
          isOpen={showNewVersionDialog}
          onClose={handleCancelNewVersion}
          title={`Create ${selectedLanguage === "python" ? "Python" : "JavaScript"} Function`}
          size="md"
        >
          <View className="space-y-4 p-4">
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Create a new {selectedLanguage === "python" ? "Python" : "JavaScript"} function with a unique name.
            </Text>

            <InputField
              label="Function Name"
              placeholder="Enter a unique name for your function"
              value={newVersionName}
              onChange={(value: string) => setNewVersionName(value)}
              error={newVersionName.trim().length > 0 && !isNameUnique ? "A function with this name already exists" : undefined}
            />

            <View className="flex justify-end space-x-3 pt-4">
              <Button
                variant="ghost"
                onClick={handleCancelNewVersion}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                variant="brand"
                onClick={handleCreateFunction}
                disabled={!canCreateVersion || isCreating}
                isLoading={isCreating}
              >
                Create Function
              </Button>
            </View>
          </View>
        </Dialog>
      )}
    </motion.div>
  );
};
